import { useEffect, useMemo, useRef } from 'react'
import { HocuspocusProvider } from '@hocuspocus/provider'
import * as Y from 'yjs'

import { COLLAB_URL } from '@/lib/config'

interface UseCollaborationOptions {
  documentName: string
  userName: string
  userColor?: string
  token?: string
  enabled?: boolean
}

interface UseCollaborationResult {
  provider: HocuspocusProvider | null
  ydoc: Y.Doc
  isConnected: boolean
  isSynced: boolean
}

// Non-empty tuple so `USER_COLORS[0]` is provably present and can act as the
// fallback for the random index under noUncheckedIndexedAccess.
const USER_COLORS: [string, ...Array<string>] = [
  '#958DF1',
  '#F98181',
  '#FBBC88',
  '#FAF594',
  '#70CFF8',
  '#94FADB',
  '#B9F18D',
  '#E8A0BF',
]

function getRandomColor(): string {
  return USER_COLORS[Math.floor(Math.random() * USER_COLORS.length)] ?? USER_COLORS[0]
}

export function useCollaboration({
  documentName,
  userName,
  userColor,
  token,
  enabled = true,
}: UseCollaborationOptions): UseCollaborationResult {
  const providerRef = useRef<HocuspocusProvider | null>(null)
  const ydoc = useMemo(() => new Y.Doc(), [])
  const color = useMemo(() => userColor || getRandomColor(), [userColor])

  useEffect(() => {
    if (!enabled || !documentName) return

    const provider = new HocuspocusProvider({
      url: COLLAB_URL,
      name: documentName,
      document: ydoc,
      token: token || '',
      onAuthenticationFailed: ({ reason }) => {
        console.error('[Collaboration] Auth failed:', reason)
      },
    })

    // Set user awareness (cursor info)
    provider.setAwarenessField('user', {
      name: userName,
      color,
    })

    providerRef.current = provider

    return () => {
      provider.destroy()
      providerRef.current = null
    }
  }, [documentName, userName, color, token, enabled, ydoc])

  const current = providerRef.current as any
  return {
    provider: providerRef.current,
    ydoc,
    isConnected: current?.status === 'connected',
    isSynced: current?.isSynced ?? false,
  }
}
